import { Link } from '@inertiajs/react'
import { Calendar, Clock, ArrowRight } from 'lucide-react'
import type { BlogPostCard as BlogPostCardType } from '@/types'

type Props = {
  post: BlogPostCardType
  layout?: 'compact' | 'featured'
}

export default function BlogPostCard({ post, layout = 'compact' }: Props) {
  const isFeatured = layout === 'featured'

  return (
    <Link
      href={`/blog/${post.slug}`}
      className={`group block overflow-hidden rounded-2xl bg-card shadow-sm ring-1 ring-black/5 transition hover:shadow-lg hover:ring-primary/20 ${
        isFeatured ? 'md:grid md:grid-cols-2' : ''
      }`}
    >
      <div
        className={`relative overflow-hidden ${
          isFeatured ? 'h-64 md:h-full' : 'h-44'
        } ${post.cover_image_url ? '' : 'bg-primary/5'}`}
      >
        {post.cover_image_url ? (
          <>
            <img
              src={post.cover_image_url}
              alt=""
              className="h-full w-full object-cover transition group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-linear-to-t from-black/30 to-transparent" />
          </>
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Calendar className="size-10 text-primary/25" />
          </div>
        )}
        {post.category && (
          <span className="absolute top-3 left-3 rounded-full bg-background/90 px-2.5 py-1 text-xs font-medium text-foreground backdrop-blur-sm">
            {post.category.name}
          </span>
        )}
      </div>

      <div className={`flex flex-col ${isFeatured ? 'justify-center p-6 md:p-8' : 'p-5'}`}>
        <h3
          className={`font-serif font-semibold tracking-tight text-foreground ${
            isFeatured ? 'text-2xl md:text-3xl' : 'text-lg'
          }`}
        >
          {post.title}
        </h3>
        <p
          className={`mt-2 text-foreground/80 ${
            isFeatured ? 'line-clamp-3 text-base' : 'line-clamp-2 text-sm'
          }`}
        >
          {post.excerpt}
        </p>
        <div className="mt-4 flex items-center justify-between gap-3 text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            {post.published_at && <PublishedDate iso={post.published_at} />}
            <span className="flex items-center gap-1">
              <Clock className="size-3" />
              {post.reading_time_minutes} min
            </span>
          </div>
          <ArrowRight className="size-4 text-primary opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
        </div>
        {post.byline && (
          <p className="mt-2 text-xs font-medium text-muted-foreground">{post.byline}</p>
        )}
      </div>
    </Link>
  )
}

function PublishedDate({ iso }: { iso: string }) {
  const date = new Date(iso)
  const text = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
  return (
    <span className="flex items-center gap-1">
      <Calendar className="size-3" />
      {text}
    </span>
  )
}
